import React, { useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { CSSProperties } from 'styled-components/macro';
import CountryCodePicker from 'lib/components/country-code-picker/CountryCodePicker';
import { FormikErrorMessage } from './FormikErrorMessage';
import { FormikLabel } from './FormikLabel';
import { Flex, FlexProps } from '../styles/layout';
import {
  combinePhone,
  parseLocalNumber,
  CountryCode,
} from 'app/pages/admin/users/components/userDetails/ContactSection';
import { UserValues } from 'app/pages/account/userAccount/editMyProfile/components/userValidator';
import { TextInput } from './FormikInputField';

interface FormikPhoneFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  info?: string | React.ReactNode;
  isRequired: boolean;
  extendStyles?: {
    errorWrapper?: CSSProperties;
    labelStyles?: CSSProperties;
    inputWrapper?: FlexProps;
    mainWrapper?: FlexProps;
  };
  country: string;
  setCountry: (country: CountryCode) => void;
}

export const FormikPhoneInputField = React.forwardRef<
  HTMLInputElement,
  FormikPhoneFieldProps
>(
  (
    { label, isRequired, extendStyles, info, country, setCountry, ...props },
    ref
  ) => {
    const [field, meta] = useField(props);
    const { setFieldValue, values } = useFormikContext<UserValues>();

    const [local, setLocal] = useState<string>(
      parseLocalNumber(field.value || '')
    );

    useEffect(() => {
      const localParsed = parseLocalNumber(field.value || '');
      setLocal(localParsed);
    }, []);

    const handleCountryChange = (newCountry: CountryCode) => {
      setCountry(newCountry);
      const newFull = combinePhone(newCountry as CountryCode, local);
      setFieldValue(props.name, newFull);

      if (props.name === 'phone1') {
        const phone2Local = parseLocalNumber(values.phone2 || '');
        const cellLocal = parseLocalNumber(values.cellPhone || '');
        setFieldValue(
          'phone2',
          combinePhone(newCountry as CountryCode, phone2Local)
        );
        setFieldValue(
          'cellPhone',
          combinePhone(newCountry as CountryCode, cellLocal)
        );
      }
    };

    const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newLocal = e.target.value;
      const valid = /^[0-9\s\-().]*$/.test(newLocal);
      if (!valid) return;
      setLocal(newLocal);
      setFieldValue(props.name, combinePhone(country as CountryCode, newLocal));
    };

    return (
      <Flex {...extendStyles?.mainWrapper} gap='4px'>
        {label && (
          <FormikLabel
            label={label}
            name={props.name}
            isRequired={isRequired}
            disabled={!!props.disabled}
            info={info}
            labelStyles={extendStyles?.labelStyles}
          />
        )}
        <Flex gap='12px' flexDirection='column'>
          <Flex {...extendStyles?.inputWrapper} gap='12px' flexDirection='row'>
            <CountryCodePicker
              ext={country}
              setExtension={handleCountryChange}
            />
            <TextInput
              {...field}
              {...props}
              id={props.name}
              ref={ref}
              value={local}
              onChange={handleLocalChange}
            />
          </Flex>
          <FormikErrorMessage meta={meta} extendStyles={extendStyles} />
        </Flex>
      </Flex>
    );
  }
);

FormikPhoneInputField.displayName = 'FormikPhoneInputField';
